import * as React from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { Link, useNavigate } from "react-router-dom";
import SignoutHeader from "../subcomponents/SignoutHeader";
import { SecondaryButton } from "../subcomponents/CustomButton";
import { useEffect } from "react";
import { useSelector } from "react-redux";
import { api } from "../utils/utils";
import { RootState } from "../store";
import { QueuePatient, QueuePatientState } from "../types";

const AutoAddQueue: React.FC = () => {
  const user = useSelector((state: RootState) => state.auth.user);
  const [queue, setQueue] = React.useState<QueuePatient[]>([]);
  const navigate = useNavigate();
  const [loading, setLoading] = React.useState<boolean>(true);

  React.useEffect(() => {
    const fetchQueue = async () => {
      if (!user?.token) {
        setQueue([]);
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        const response = await api.get(`/patients/queue`, user.token);
        setQueue(response.data || []);
      } catch (error) {
        console.error("Error fetching queue:", error);
        setQueue([]);
      } finally {
        setLoading(false);
      }
    };
    fetchQueue();
  }, [user?.token]);

  const inProgress = queue.filter((item) => !item.done);
  const completed = queue.filter((item) => item.state === QueuePatientState.added);
  const failed = queue.filter((item) => item.state === QueuePatientState.aborted);

  const getStatusLabel = (item: QueuePatient) => {
    if (!item.done) return "In progress";
    if (item.state === QueuePatientState.aborted) return "Error";
    if (item.state === QueuePatientState.added) return "Completed";
    return item.state;
  };

  const getStatusColor = (item: QueuePatient): "default" | "success" | "error" => {
    if (!item.done) return "default";
    if (item.state === QueuePatientState.aborted) return "error";
    if (item.state === QueuePatientState.added) return "success";
    return "default";
  };

  const sortedQueue: QueuePatient[] = React.useMemo(() => {
    const priority = (item: QueuePatient) => {
      if (!item.done) return 0; // in progress
      if (item.state === QueuePatientState.aborted) return 1; // error
      if (item.state === QueuePatientState.added) return 2; // completed
      return 3;
    };
    return [...queue].sort((a, b) => priority(a) - priority(b));
  }, [queue]);

  return (
    <Box>
      <SignoutHeader />
      <Box
        sx={{
          maxWidth: 1200,
          mx: "auto",
          px: 3,
          py: 3
        }}
      >
        <Box sx={{ mb: 2 }}>
          <Link to="/dashboard/patients/auto-add" style={{ textDecoration: "none" }}>
            <SecondaryButton
              sx={{
                display: { xs: "none", lg: "flex" },
                alignItems: "center",
                mb: 1
              }}
            >
              <ArrowBackIcon sx={{ mr: 1 }} /> Back
            </SecondaryButton>
          </Link>
        </Box>

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            mb: 3,
            gap: 2,
            flexWrap: "wrap"
          }}
        >
          <Box>
            <Typography variant="h5" sx={{ mb: 0.5 }}>
              Auto-add queue
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Monitor patients being added from your recommended list.
            </Typography>
          </Box>
          <Box sx={{ textAlign: "right" }}>
            <Typography variant="body2" color="text.secondary">
              {loading
                ? "Loading queue…"
                : queue.length === 0
                ? "No patients in the queue"
                : `${inProgress.length} in progress • ${failed.length} errors • ${completed.length} completed`}
            </Typography>
          </Box>
        </Box>

        {loading ? (
          <Paper
            variant="outlined"
            sx={{
              p: 4,
              borderRadius: 2,
              boxShadow: "none",
              textAlign: "center",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 2
            }}
          >
            <CircularProgress size={24} />
            <Typography variant="body2" color="text.secondary">
              Loading auto-add queue…
            </Typography>
          </Paper>
        ) : queue.length === 0 ? (
          <Paper
            variant="outlined"
            sx={{ p: 3, borderRadius: 2, boxShadow: "none", textAlign: "center" }}
          >
            <Typography variant="body2" color="text.secondary">
              There are no patients in the auto-add queue yet.
            </Typography>
          </Paper>
        ) : (
          <Paper
            variant="outlined"
            sx={{ borderRadius: 2, boxShadow: "none", borderColor: "divider" }}
          >
            <TableContainer>
              <Table size="small" sx={{ "& .MuiTableBody-root .MuiTableRow-root:last-child .MuiTableCell-root": { borderBottom: 0 } }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Patient</TableCell>
                    <TableCell>ID</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Error message</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedQueue.map((item) => (
                    <TableRow key={item.queuePatientId}>
                      <TableCell>
                        <Typography variant="body2">
                          {item.name}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {item.webptPatientId}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={getStatusLabel(item)}
                          color={getStatusColor(item)}
                          variant={getStatusColor(item) === "default" ? "outlined" : "filled"}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {item.state === QueuePatientState.aborted ? item.error || "-" : "-"}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        )}
      </Box>
    </Box>
  );
};

export default AutoAddQueue;
